import { type LucideIcon } from "lucide-react";
import { type ReactNode } from "react";

import { PageHero } from "@/components/layout/PageHero";
import { cn } from "@/lib/utils";
import { typeBodyClass, typeProseClass, typeTitleClass } from "@/lib/typography";

interface ContentPageProps {
    title: string;
    description?: string;
    eyebrow?: string;
    icon?: LucideIcon;
    /** Rendered under the intro, before the sections — e.g. a last-updated note. */
    note?: ReactNode;
    children: ReactNode;
    className?: string;
}

/**
 * Shell for long-form reading pages (policy, FAQ, about).
 *
 * These pages used to bring their own marketing header and stack
 * `border-2 shadow-lg` cards with gradient text on top of the app shell —
 * four visual languages competing inside one route. This drops the private
 * header entirely and lets the app chrome do its job, so the page reads as
 * part of the product.
 *
 * The measure is capped on two axes. `max-w-3xl` caps the column, and each
 * paragraph inside a section caps again at `68ch` — a policy paragraph at the
 * full 768px column is ~100 characters, which is beyond the point where the eye
 * can find the next line. The cap sits on the paragraph, not the column, so
 * chip rows and lists keep the full width.
 *
 * On a phone this page also gets its own 12px of horizontal padding on top of
 * the shell's 8px: long-form text that runs to within 8px of the viewport edge
 * is the hardest reading on the app, and reading pages are the one place the
 * dense card grid's gutter is wrong.
 */
const ContentPage = ({
    title,
    description,
    eyebrow,
    icon,
    note,
    children,
    className,
}: ContentPageProps) => (
    <div className={cn("space-y-6", className)}>
        <PageHero
            title={title}
            description={description ?? ""}
            eyebrow={eyebrow}
            icon={icon}
        />

        {/* Reading pages get a wider gutter than the card pages: `PublicLayout`
            gives every public page 8px below `sm`, which is right for a grid of
            cards and wrong for a column of prose — the text ran to within 8px of
            the viewport edge. This adds 12px so the hero card and the reading
            column share one inset. */}
        <div className="mx-auto w-full max-w-3xl space-y-4 px-3 sm:px-0">
            {note}
            {children}
        </div>
    </div>
);

interface ContentSectionProps {
    id?: string;
    title: string;
    icon?: LucideIcon;
    children: ReactNode;
    /** Collapsible sections (FAQ) render a trigger instead of a static head. */
    className?: string;
}

/**
 * One numbered/lettered section of a reading page.
 *
 * A hairline-divided band rather than a card: on a page of ten sections,
 * cards turn the document into a stack of boxes and make it hard to see how
 * the pieces relate. Dividers keep it a document.
 */
const ContentSection = ({ id, title, icon: Icon, children, className }: ContentSectionProps) => (
    <section
        id={id}
        className={cn("scroll-mt-24 border-t border-stone-200/70 pt-6 dark:border-stone-800", className)}
    >
        <h2 className={cn(typeTitleClass, "mb-3 flex items-center gap-2.5")}>
            {Icon && (
                <span className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                    <Icon className="h-3.5 w-3.5" aria-hidden="true" />
                </span>
            )}
            {title}
        </h2>
        {/* Long-form paragraphs cap at a readable measure; chip rows and other
            blocks below them keep the full column width, so the cap lives on
            the paragraph rather than on this wrapper. */}
        <div className={cn(typeProseClass, "[&_p]:max-w-[58ch]")}>{children}</div>
    </section>
);

/** A short labelled note, e.g. "Last updated: 1 October 2026". */
const ContentNote = ({ children }: { children: ReactNode }) => (
    <p className={cn(typeBodyClass, "text-xs")}>{children}</p>
);

export { ContentPage, ContentSection, ContentNote };
export type { ContentPageProps, ContentSectionProps };
